import { createAction, createAsyncAction } from "typesafe-actions";

import type { FetchError } from "app/pages/sales-intelligence/types";

import type { Integration } from "../../management/types";
import type {
    ActionsPanelFilters,
    ContactExportPreview,
    ContactIssueModalPayload,
    ContactSearchEntry,
    ContactSearchEntryWithKey,
    ContactsQuotaActionPayload,
    ContactsSearchSortBy,
    ContactsSearchTableFilters,
    CRMEntity,
    RevealLimitReachedModalPayload,
} from "../types/common";
import type {
    ContactsSearchCountResponseDto,
    ContactsSearchResponseDto,
    ContactsSearchResponseWithTotalCountDto,
} from "../types/dto";
import type { ExportFlowOrigin, ExportFlowPhase, PendingRevealInfo } from "../types/exportFlow";

export const tableFiltersChangedAction = createAction("@@si/contacts-search/table-filters-changed")<
    Partial<ContactsSearchTableFilters>
>();

export const tableSortingChangedAction = createAction("@@si/contacts-search/table-sorting-changed")<
    Partial<ContactsSearchSortBy>
>();

export const tableFiltersClearedAction = createAction("@@si/contacts-search/table-filters-cleared")();

export const actionsPanelFiltersChangedAction = createAction("@@si/contacts-search/actions-panel-filters-changed")<
    Partial<ActionsPanelFilters>
>();

export const toggleIncludeOnlyNotRevealed = createAction(
    "@@si/contacts-search/toggle-include-only-not-revealed",
)<boolean>();

export const tableRowsSelectionToggledAction = createAction(
    "@@si/contacts-search/table-rows-selection-toggled",
)<boolean>();

export const tableRowsSelectionChangedAction = createAction("@@si/contacts-search/table-rows-selection-changed")<
    ContactSearchEntryWithKey[]
>();

// don't dispatch this action directly - use updateUsedContactsQuota effect
export const usedQuotaUpdatedAction = createAction("@@si/contacts-search/used-quota-updated-changed")<number>();

export const revealLimitReachedModalToggledAction = createAction(
    "@@si/contacts-search/reveal-limit-reached-modal-toggled",
)<RevealLimitReachedModalPayload>();

export const contactIssueModalToggledAction = createAction(
    "@@si/contacts-search/contact-issue-modal-toggled",
)<ContactIssueModalPayload>();

export const contactReportedAction = createAction("@@si/contacts-search/contact-reported")<string>();

export const searchResultsClearedAction = createAction("@@si/contacts-search/search-results-cleared")<{
    areResultsFetching: boolean;
}>();

export const userContactsQuotaFetchedAsync = createAsyncAction(
    "@@si/contacts-search/user-quota-fetch-started",
    "@@si/contacts-search/user-quota-fetch-succeeded",
    "@@si/contacts-search/user-quota-fetch-failed",
)<void, ContactsQuotaActionPayload, FetchError>();

export const contactsSearchResultsFetchedAsync = createAsyncAction(
    "@@si/contacts-search/results-fetch-started",
    "@@si/contacts-search/results-fetch-succeeded",
    "@@si/contacts-search/results-fetch-failed",
)<void, ContactsSearchResponseDto, FetchError>();

export const contactsSearchResultsTotalCountFetchedAsync = createAsyncAction(
    "@@si/contacts-search/results-total-count-fetch-started",
    "@@si/contacts-search/results-total-count-fetch-succeeded",
    "@@si/contacts-search/results-total-count-fetch-failed",
)<void, ContactsSearchCountResponseDto, FetchError>();

export const contactsSearchResultsWithTotalCountFetchedAsync = createAsyncAction(
    "@@si/contacts-search/results-with-total-count-fetch-started",
    "@@si/contacts-search/results-with-total-count-fetch-succeeded",
    "@@si/contacts-search/results-with-total-count-fetch-failed",
)<void, ContactsSearchResponseWithTotalCountDto, FetchError>();

export const multipleContactsWereRevealedAsync = createAsyncAction(
    "@@si/contacts-search/multiple-contacts-reveal-started",
    "@@si/contacts-search/multiple-contacts-reveal-succeeded",
    "@@si/contacts-search/multiple-contacts-reveal-failed",
)<void, number, FetchError>();

export const exportedToExcelAsync = createAsyncAction(
    "@@si/contacts-search/export-to-excel-started",
    "@@si/contacts-search/export-to-excel-succeeded",
    "@@si/contacts-search/export-to-excel-failed",
)<void, void, FetchError>();

export const initExportToExcel = createAction("@@si/contacts-search/init-export-to-excel")();
export const initExportToCrm = createAction("@@si/contacts-search/init-export-to-crm")<{
    crm: Integration;
    as: CRMEntity;
}>();

export const fetchExportToCrmPreviewAsync = createAsyncAction(
    "@@si/contacts-search/fetch-export-to-crm-preview-started",
    "@@si/contacts-search/fetch-export-to-crm-preview-succeeded",
    "@@si/contacts-search/fetch-export-to-crm-preview-failed",
)<void, ContactExportPreview[], void>();

export const contactExportPreviewChanged = createAction(
    "@@si/contacts-search/contact-export-preview-changed",
)<ContactExportPreview>();

export const contactExportPreviewDeleted = createAction(
    "@@si/contacts-search/contact-export-preview-deleted",
)<string>();

export const toggleContactUsHook = createAction("@@si/contacts-search/toggle-contact-us-hook")();

export const setIsActionPanelActivated = createAction("@@si/contacts-search/set-is-action-panel-activated")<boolean>();

export const resetExportToCrm = createAction("@@si/contacts-search/clear-export-to-crm")();

export const setRequestedContactsForExport = createAction("@@si/contacts-search/set-requested-contacts-for-export")<
    readonly ContactSearchEntry[]
>();
export const resetRequestedContactsForExport = createAction(
    "@@si/contacts-search/clear-requested-contacts-for-export",
)();

export const setPendingRevealInfo = createAction("@@si/contacts-search/set-pending-reveal-info")<PendingRevealInfo>();

export const clearPendingRevealInfo = createAction("@@si/contacts-search/clear-pending-reveal-info")();

export const setExportFlowPhase = createAction("@@si/contacts-search/set-export-flow-phase")<ExportFlowPhase>();

export const setExportFlowOrigin = createAction("@@si/contacts-search/set-export-flow-origin")<ExportFlowOrigin | null>();
